import { useEffect, useState } from 'react';
import { Shield } from 'lucide-react';
import toast from 'react-hot-toast';
import { authFetch } from '../services/api';
import CategoryPanel from '../components/identity/CategoryPanel';
import UploadDocModal from '../components/identity/UploadDocModal';

export default function IdentityVault() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSpv, setShowSpv] = useState(
    () => localStorage.getItem('vault_show_spv') === 'true'
  );
  const [uploadCategory, setUploadCategory] = useState(null);

  const fetchVault = () => {
    setLoading(true);
    authFetch('/api/documents/personal')
      .then((r) => (r.ok ? r.json() : []))
      .then(setGroups)
      .finally(() => setLoading(false));
  };

  useEffect(fetchVault, []);

  const toggleSpv = () => {
    const next = !showSpv;
    setShowSpv(next);
    localStorage.setItem('vault_show_spv', String(next));
  };

  const personalGroups = groups.filter((g) => g.group === 'personal');
  const companyGroups = groups.filter((g) => g.group === 'company');

  const allDocs = groups.flatMap((g) => g.documents);
  const totals = {
    count: allDocs.length,
    expiring: allDocs.filter((d) => d.status === 'expiring_soon').length,
    expired: allDocs.filter((d) => d.status === 'expired').length,
  };

  return (
    <div className="p-6 space-y-5 max-w-5xl">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
          <Shield size={20} className="text-emerald-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">Identity & verification</h1>
          <p className="text-slate-400 text-sm">
            Documents that prove who you are and the state of your finances.
            Shared with the broker only when you opt them in below.
          </p>
        </div>
      </div>

      {!loading && totals.count > 0 && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-300">
          <strong>{totals.count}</strong> {totals.count === 1 ? 'document' : 'documents'}
          {totals.expiring > 0 && (
            <> · <span className="text-amber-300">{totals.expiring} expiring soon</span></>
          )}
          {totals.expired > 0 && (
            <> · <span className="text-rose-300">{totals.expired} expired</span></>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-center py-20 text-slate-500">Loading…</div>
      ) : (
        <>
          <section>
            <h2 className="text-lg font-semibold text-white mb-3">Personal verification</h2>
            <div className="space-y-3">
              {personalGroups.map((g) => (
                <CategoryPanel
                  key={g.key}
                  group={g}
                  onUploadClick={() => setUploadCategory(g)}
                  onChanged={fetchVault}
                />
              ))}
            </div>
          </section>

          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-white">Limited company (SPV)</h2>
              <button
                onClick={toggleSpv}
                className="text-sm text-slate-400 hover:text-emerald-400"
              >
                {showSpv ? 'I do not invest through a company' : 'I invest through a Ltd'}
              </button>
            </div>
            {showSpv && (
              <div className="space-y-3">
                {companyGroups.map((g) => (
                  <CategoryPanel
                    key={g.key}
                    group={g}
                    onUploadClick={() => setUploadCategory(g)}
                    onChanged={fetchVault}
                  />
                ))}
              </div>
            )}
            {!showSpv && (
              <p className="text-sm text-slate-500">
                If any of your investments are held inside a UK limited company, click
                "I invest through a Ltd" above to reveal the company-document panels
                (Companies House cert, articles, accounts, VAT cert).
              </p>
            )}
          </section>
        </>
      )}

      {uploadCategory && (
        <UploadDocModal
          group={uploadCategory}
          onClose={() => setUploadCategory(null)}
          onUploaded={() => {
            setUploadCategory(null);
            fetchVault();
            toast.success('Document uploaded');
          }}
        />
      )}
    </div>
  );
}
